/**
 * Vista Lotes — visor consolidado de ingresos manuales.
 *
 * Cada `ingresoId` agrupa series que fueron creadas en el mismo batch.
 * Muestra estado de uso (disponible / parcial / agotado) calculado en runtime.
 * Permite eliminar el lote completo si todas las series están intactas
 * (sin movimientos posteriores al alta_manual).
 */

import { useState, useMemo } from 'react';
import { Loader2, Trash2, Tag, Lock, AlertCircle } from 'lucide-react';
import { toast } from '@/lib/notify';
import {
  useSeries,
  useMovimientos,
  useProductos,
  useUbicaciones,
  useEliminarIngresoMutation,
} from '@/api/queries';
import { EtiquetasGs1Modal } from '@/features/etiquetas/EtiquetasGs1Modal';
import { useSessionStore } from '@/stores/session';
import { useAuditStore } from '@/stores/audit';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { fmtDate, fmtNum } from '@/lib/format';
import {
  FirmaElectronicaModal,
  type FirmaElectronicaResult,
} from '@/features/auth/FirmaElectronicaModal';
import { puedeActuar } from '@/domain/rbac';

type EstadoLote = 'disponible' | 'parcial' | 'agotado';

interface LoteInfo {
  ingresoId: string;
  gtin: string;
  lote: string;
  vencimiento: string;
  ubicacionId: string;
  createdAt: string;
  cantidadTotal: number;
  cantidadUsadas: number;
  estado: EstadoLote;
}

export function LotesPage() {
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const usuario = useSessionStore((s) => s.usuario);
  const addAuditEntry = useAuditStore((s) => s.addEntry);

  const { data: series = [], isLoading } = useSeries();
  const { data: movimientos = [] } = useMovimientos();
  const { data: productos = [] } = useProductos();
  const { data: ubicaciones = [] } = useUbicaciones();
  const mutation = useEliminarIngresoMutation();

  const [loteAEliminar, setLoteAEliminar] = useState<LoteInfo | null>(null);
  const [loteEtiquetas, setLoteEtiquetas] = useState<LoteInfo | null>(null);

  const seriesDelLote = useMemo(() => {
    if (!loteEtiquetas) return [];
    return series.filter((s) => s.ingresoId === loteEtiquetas.ingresoId);
  }, [series, loteEtiquetas]);
  const productoDelLote = useMemo(() => {
    if (!loteEtiquetas) return null;
    return productos.find((p) => p.gtin === loteEtiquetas.gtin) ?? null;
  }, [productos, loteEtiquetas]);

  const lotes: LoteInfo[] = useMemo(() => {
    const map = new Map<string, LoteInfo>();
    for (const s of series) {
      if (!s.ingresoId) continue;
      const movs = movimientos.filter((m) => m.serieId === s.id);
      const usada = movs.length > 1 || s.tenencia !== 'en_poder';
      const existing = map.get(s.ingresoId);
      if (existing) {
        existing.cantidadTotal++;
        if (usada) existing.cantidadUsadas++;
      } else {
        map.set(s.ingresoId, {
          ingresoId: s.ingresoId,
          gtin: s.gtin,
          lote: s.lote,
          vencimiento: s.vencimiento,
          ubicacionId: s.ubicacionActualId ?? '',
          createdAt: s.createdAt,
          cantidadTotal: 1,
          cantidadUsadas: usada ? 1 : 0,
          estado: 'disponible',
        });
      }
    }
    return Array.from(map.values())
      .map<LoteInfo>((l) => ({
        ...l,
        estado:
          l.cantidadUsadas === 0
            ? 'disponible'
            : l.cantidadUsadas === l.cantidadTotal
              ? 'agotado'
              : 'parcial',
      }))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [series, movimientos]);

  const handleClickEliminar = (lote: LoteInfo) => {
    if (!puedeActuar(usuario, 'eliminar_ingreso')) {
      addAuditEntry({
        categoria: 'sesion',
        accion: 'permiso.denegado',
        detalle: 'Intento de eliminar lote sin permiso',
      });
      toast.error('No tenés permiso para eliminar lotes');
      return;
    }
    if (lote.estado !== 'disponible') {
      toast.error('Solo se pueden eliminar lotes "Disponibles" (sin series usadas)');
      return;
    }
    setLoteAEliminar(lote);
  };

  const handleFirmaEliminar = (firma: FirmaElectronicaResult) => {
    const lote = loteAEliminar;
    setLoteAEliminar(null);
    if (!firma.firmado || !lote || !usuario || !firma.razon) return;
    mutation.mutate(
      { ingresoId: lote.ingresoId, usuarioId: usuario.id, razon: firma.razon },
      {
        onSuccess: (data) => {
          addAuditEntry({
            categoria: 'operacion_critica',
            accion: 'serie.eliminar_ingreso',
            entidadTipo: 'ingreso',
            entidadId: lote.ingresoId,
            valoresAntes: {
              gtin: lote.gtin,
              lote: lote.lote,
              cantidad: lote.cantidadTotal,
              ubicacionId: lote.ubicacionId,
            },
            razon: firma.razon,
            significadoFirma: firma.significado,
          });
          toast.success(`Lote ${lote.ingresoId} eliminado · ${data.eliminadas} series`);
        },
        onError: (err) => toast.error(`Error: ${err.message}`),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando lotes...
      </div>
    );
  }

  // Guard de página — Operador no tiene acceso (paquete L.1). El sidebar oculta
  // el item pero el usuario podría llegar vía URL directa.
  if (!puedeActuar(usuario, 'ver_lotes')) {
    return (
      <Card>
        <CardContent className="space-y-3 py-12 text-center">
          <Lock className="mx-auto h-8 w-8 text-gray-600" />
          <div className="text-sm font-semibold text-gray-700">
            Acceso restringido
          </div>
          <div className="mx-auto max-w-md text-xs text-gray-600">
            La gestión de lotes está limitada al rol <b>Administrador</b>. Las series
            individuales seguís pudiendo verlas en <b>Series</b>.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Alert className="mb-4 border-amber-300 bg-amber-50">
        <AlertCircle className="h-4 w-4 text-amber-700" />
        <AlertDescription className="text-xs text-amber-900">
          <b>Feature prevista para v2 (post-MVP).</b> La gestión de lotes funciona contra el
          mock para validar UX, pero el backend real <b>no se implementa en v1</b>. Si en v1
          se requiere cargar series iniciales, se hace por un script de migración directo a
          la DB — ver Handoff técnico.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-everex-azul">
            Lotes (altas manuales)
            <span className="ml-2 rounded bg-amber-200 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-800">
              v2
            </span>
          </CardTitle>
          <p className="text-xs text-gray-600">
            Visor consolidado de todos los ingresos manuales. Los lotes "Disponibles" (sin series
            usadas) pueden eliminarse si fueron creados por error. La acción queda registrada
            en el audit trail.
          </p>
        </CardHeader>
        <CardContent>
          {lotes.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-600">
              Aún no hay altas manuales. Creá un lote desde la vista "Ingreso / Alta".
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-gray-100">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ingreso ID</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead>Lote / Vto</TableHead>
                    <TableHead>Ubicación</TableHead>
                    <TableHead>Cantidad</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lotes.map((l) => {
                    const prod = productos.find((p) => p.gtin === l.gtin);
                    const ub = ubicaciones.find((u) => u.id === l.ubicacionId);
                    const enMiGln = l.ubicacionId === ubicacionActivaId;
                    const eliminable = l.estado === 'disponible' && enMiGln;
                    return (
                      <TableRow
                        key={l.ingresoId}
                        className={cn(!enMiGln && 'opacity-60')}
                      >
                        <TableCell className="font-mono text-[11px]">{l.ingresoId}</TableCell>
                        <TableCell className="text-xs">{prod?.nombre ?? l.gtin}</TableCell>
                        <TableCell className="text-xs">
                          <div className="font-mono">{l.lote}</div>
                          <div className="text-[11px] text-gray-600">{l.vencimiento}</div>
                        </TableCell>
                        <TableCell className="text-xs">{ub?.nombre ?? '—'}</TableCell>
                        <TableCell className="text-xs">
                          <b>{fmtNum(l.cantidadUsadas)}</b> / {fmtNum(l.cantidadTotal)}
                        </TableCell>
                        <TableCell>
                          <span
                            className={cn(
                              'badge',
                              l.estado === 'disponible'
                                ? 'badge-green'
                                : l.estado === 'parcial'
                                  ? 'badge-yellow'
                                  : 'badge-gray',
                            )}
                          >
                            {l.estado === 'disponible'
                              ? 'Disponible'
                              : l.estado === 'parcial'
                                ? 'Parcial'
                                : 'Agotado'}
                          </span>
                        </TableCell>
                        <TableCell className="text-[11px] text-gray-600">
                          {fmtDate(l.createdAt)}
                        </TableCell>
                        <TableCell className="space-x-1 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setLoteEtiquetas(l)}
                            className="text-everex-azul hover:bg-everex-azul-bg"
                            title="Imprimir / descargar etiquetas GS1 del lote"
                          >
                            <Tag className="mr-1 h-3 w-3" /> Etiquetas
                          </Button>
                          {eliminable && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleClickEliminar(l)}
                              disabled={mutation.isPending}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="mr-1 h-3 w-3" /> Eliminar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          <Alert className="mt-4 border-amber-200 bg-amber-50">
            <AlertDescription className="text-xs text-amber-900">
              <b>Nota:</b> solo se pueden eliminar lotes en tu GLN activo y con todas las
              series sin usar. Si una serie ya fue despachada o pickeada, el lote queda
              "Parcial" o "Agotado" y no puede borrarse.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <FirmaElectronicaModal
        open={loteAEliminar !== null}
        titulo="Eliminar lote de ingreso"
        resumen={
          loteAEliminar
            ? `Lote ${loteAEliminar.ingresoId} · ${loteAEliminar.cantidadTotal} series del GTIN ${loteAEliminar.gtin} · Lote ${loteAEliminar.lote}. Esta acción es IRREVERSIBLE.`
            : ''
        }
        requiereRazon
        onResolve={handleFirmaEliminar}
      />

      {/* Modal de etiquetas GS1 — re-imprimir o descargar PDF del lote */}
      <EtiquetasGs1Modal
        open={loteEtiquetas !== null}
        onClose={() => setLoteEtiquetas(null)}
        producto={productoDelLote}
        series={seriesDelLote}
        contexto={
          loteEtiquetas
            ? `Lote ${loteEtiquetas.ingresoId} · ${seriesDelLote.length} series · DataMatrix GS1`
            : undefined
        }
      />
    </>
  );
}
